import { useEffect, useRef, useState } from 'react';
import { fetchFileBytesShared } from '../../lib/file-bytes-cache';
import { usePreviewNavStore } from '../../lib/preview-nav';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 그려진 쪽들을 **썸네일 레일**(PreviewThumbRail)에 등록한다 (2026-08-14 2차).
 *
 * 레일은 스스로 아무것도 그리지 않는다 — `lib/preview-nav` 레지스트리에 "몇 장인지 ·
 * 어떻게 이동하는지 · 썸네일을 어떻게 그리는지" 를 올려 두면 구독해서 쓴다(PDF·PPT 가 이미
 * 그렇게 붙어 있다). 한글은 엔진이 **SVG 를 DOM 에 다 그려 둔** 상태라, 썸네일은 그 SVG 를
 * 복제해 폭만 맞추면 된다 — 엔진을 다시 부르지 않는다(문서는 이미 free 됐다).
 *
 * ⚠ 복제본은 원본과 **같은 id** 를 갖는다. SVG 내부 `url(#…)` 참조는 문서에서 처음 만난
 *   요소를 쓰므로 원본 정의가 먼저 잡히는데, 원본이 살아 있는 동안에만 그리므로 문제가 없다.
 *   (원본이 사라지면 레일도 함께 사라진다 — 같은 컴포넌트가 소유한다.)
 */
function registerThumbRail(slot: HTMLElement, key: string): () => void {
  const pagesOf = () => Array.from(slot.querySelectorAll<HTMLElement>('.hwp-preview__page'));
  const pages = pagesOf();
  if (pages.length <= 1) return () => {};   // 한 장이면 레일이 뜻이 없다

  const { setSource, clearSource, setActiveIndex } = usePreviewNavStore.getState();
  setSource({
    key,
    count: pages.length,
    goTo: (index) => {
      pagesOf()[index]?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      setActiveIndex(index);
    },
    drawThumb: (index, host) => {
      const svg = pagesOf()[index]?.querySelector('svg');
      if (!svg) return;
      const clone = svg.cloneNode(true) as SVGElement;
      // 레일 폭(약 132px)에 맞춘다 — 엔진이 준 width/height 속성을 그대로 두면 원본 크기로 뜬다.
      clone.removeAttribute('width');
      clone.removeAttribute('height');
      clone.style.width = '100%';
      clone.style.height = 'auto';
      clone.style.display = 'block';
      host.replaceChildren(clone);
      return () => host.replaceChildren();
    },
  });

  // 스크롤에 따라 현재 쪽 강조 — 화면 위쪽에 가장 가까운 쪽을 활성으로 본다(PDF 와 같은 규칙).
  const io = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((e) => e.isIntersecting);
      if (visible.length === 0) return;
      const top = visible.reduce((a, b) => (
        a.boundingClientRect.top <= b.boundingClientRect.top ? a : b));
      const idx = pagesOf().indexOf(top.target as HTMLElement);
      if (idx >= 0) setActiveIndex(idx);
    },
    { root: slot.closest('.workspace-docpanel-body'), threshold: 0.1 },
  );
  pages.forEach((p) => io.observe(p));

  return () => { io.disconnect(); clearSource(key); };
}

/**
 * 한글 문서 **읽기 전용 미리보기**.
 *
 * 전에는 이 자리에 "편집을 누르세요" 안내만 있었다 — 경량 렌더러가 없어서 내용을 보려면
 * 편집기(스튜디오 셸 1.3MB)를 띄우는 수밖에 없었기 때문이다. `@workchat/office/hwp-preview`
 * (234KB)가 엔진만으로 그리므로 이제 목록에서 바로 훑어볼 수 있다.
 *
 * ⚠ 편집 버튼은 **그대로 둔다** — 미리보기는 상한(기본 30쪽)이 있고 편집도 못 한다.
 */
export function WorkspaceHwpPreview({ fileId }: { fileId: string }) {
  const slotRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    let disposed = false;
    let handle: { destroy(): void } | null = null;
    let detachNav: (() => void) | null = null;

    void (async () => {
      try {
        // 지연 로드 — 이 import 들이 미리보기 청크(JS+CSS)를 받아온다.
        // ⚠ CSS 를 함께 가져와야 한다. vite lib 빌드는 CSS 를 별도 파일로 뽑고 JS 안에서
        //   참조하지 않으므로, 소비자가 명시적으로 import 하지 않으면 **스타일 없이** 뜬다
        //   (빌드는 성공해서 조용히 지나간다 — 다른 편집기에서 이미 밟은 함정).
        const [{ mountHwpPreview }] = await Promise.all([
          import('@workchat/office/hwp-preview'),
          import('@workchat/office/hwp-preview.css'),
        ]);
        // 편집기·미리보기가 같은 캐시를 쓴다 — 같은 파일을 두 번 받지 않는다.
        const bytes = await fetchFileBytesShared(fileId);
        if (disposed) return;
        handle = await mountHwpPreview(slot, { bytes });
        if (disposed) { handle.destroy(); handle = null; return; }
        setLoading(false);
        detachNav = registerThumbRail(slot, `workspace:hwp:${fileId}`);
      } catch (err) {
        if (disposed) return;
        setLoading(false);
        setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      disposed = true;
      // 레일 등록을 먼저 거둔다 — DOM 이 사라진 뒤 썸네일을 그리려 들지 않게.
      detachNav?.();
      // ⚠ 반드시 놓는다 — HwpDocument 는 wasm 힙을 잡고 있어 GC 가 회수하지 못한다.
      handle?.destroy();
    };
  }, [fileId]);

  return (
    <div className="workspace-hwp-preview">
      {loading && !error && (
        <p className="workspace-hwp-preview__status">{tWorkspace('workspace.editor.hwp_preview_loading')}</p>
      )}
      {error && <p className="workspace-hwp-preview__status">{error}</p>}
      <div ref={slotRef} className="workspace-hwp-preview__slot" />
    </div>
  );
}
